import { useRef } from "react";
import pit from "@/assets/pit.jpg";
import { PillLink } from "@/components/PillLink";
import { gsap, useGSAP } from "@/lib/gsap";
import { useReducedMotion } from "@/lib/motion";
import { DOCS_URL, SIGNUP_URL } from "@/lib/site";

const NOTES = [
  { title: "One site, many scenarios", body: "A base case and as many alternatives as you need. Duplicating a scenario copies all of its configuration." },
  { title: "Quotes feed the model", body: "Pick a vendor quote for a loader or a truck and its rates are copied into the form, ready to adjust." },
  { title: "Shared, with roles and a record", body: "Viewers read. Operators enter data. Managers and admins create, delete and manage the team. Every change to a scenario is recorded with who made it." },
];

const TERMS = [
  "Collars", "Surveys", "Assays", "Bench height", "Burden", "Spacing", "Powder factor", "Stiffness ratio",
  "Rigs required", "Payload", "Cycle time", "Haul distance", "Availability", "Throughput", "Recovery", "Mine life",
];

const WORDS = ["Drill", "Blast", "Load", "Haul", "Crush"];

export function Chain() {
  const root = useRef<HTMLElement>(null);
  const reduced = useReducedMotion();

  useGSAP(
    () => {
      if (reduced) return;

      gsap.fromTo(
        "[data-chain-photo]",
        { yPercent: -9 },
        { yPercent: 9, ease: "none", scrollTrigger: { trigger: root.current, start: "top bottom", end: "bottom top", scrub: true } },
      );

      gsap.utils.toArray<HTMLElement>("[data-chain-word]").forEach((word) => {
        gsap.fromTo(
          word,
          { opacity: 0.1, xPercent: 7 },
          { opacity: 1, xPercent: 0, ease: "none", scrollTrigger: { trigger: word, start: "top 92%", end: "top 48%", scrub: true } },
        );
      });
    },
    { scope: root, dependencies: [reduced] },
  );

  return (
    <section ref={root} data-theme="dark" className="relative overflow-hidden bg-bg">
      <div className="absolute inset-0">
        <img
          data-chain-photo
          src={pit}
          alt=""
          width={1920}
          height={1080}
          loading="lazy"
          decoding="async"
          className="absolute inset-x-0 top-[-12%] h-[124%] w-full object-cover"
        />
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,#090b0a_0%,rgba(9,11,10,0.55)_18%,rgba(9,11,10,0.72)_60%,#090b0a_100%)]" />
      </div>

      <div className="shell relative pb-14 pt-24 md:pb-20 md:pt-32">
        <p data-fade className="eyebrow eyebrow-mark mb-6 text-fg/70">
          The whole chain
        </p>
        <h2 data-split className="display max-w-[11ch]">
          From first hole to final tonne.
        </h2>

        <ul className="mt-12 grid max-w-6xl gap-10 md:mt-16 md:grid-cols-3">
          {NOTES.map((note, index) => (
            <li key={note.title} data-fade data-delay={index * 0.08}>
              <span className="rule !bg-fg/30" />
              <h3 className="mt-5 text-lg font-medium tracking-[-0.02em]">{note.title}</h3>
              <p className="mt-2 text-fg/70">{note.body}</p>
            </li>
          ))}
        </ul>
      </div>

      {/* Ticker */}
      <div aria-hidden="true" className="relative border-y border-fg/15 py-4 backdrop-blur-sm">
        <div className="flex w-max animate-marquee">
          {[0, 1].map((copy) => (
            <ul key={copy} className="flex shrink-0">
              {TERMS.map((term) => (
                <li key={term} className="eyebrow flex items-center gap-8 pr-8 text-fg/80">
                  {term}
                  <span className="h-1 w-1 rounded-full bg-lime" />
                </li>
              ))}
            </ul>
          ))}
        </div>
      </div>

      <div className="shell relative pb-20 pt-16 md:pb-28 md:pt-24">
        <p className="sr-only">Drill, blast, load, haul, crush.</p>
        <div aria-hidden="true" className="flex flex-col items-end">
          {WORDS.map((word) => (
            <span
              key={word}
              data-chain-word
              className="block text-right text-[clamp(4.25rem,17.5vw,19rem)] font-medium uppercase leading-[0.84] tracking-[-0.055em]"
            >
              {word}
            </span>
          ))}
        </div>

        <div className="mt-14 flex flex-col gap-8 md:mt-20 md:flex-row md:items-end md:justify-between">
          <h2 data-split className="title max-w-[14ch]">
            Start with one site.
          </h2>
          <div data-fade className="max-w-md">
            <p className="lede text-fg/80">
              Free for 7 days with every module: a project and a site of your own, and a sample study to look around in. No card.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <PillLink to={SIGNUP_URL} label="Start free" variant="accent" arrow />
              <PillLink to={`${DOCS_URL}/quickstart`} label="Read the quickstart" variant="outline" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
